import * as React from 'react';
import * as MUI from '@locus-taxy/encore-mui';
import * as Network from '@locus-taxy/encore-ui-network';
import * as Toolkit from '@locus-taxy/encore-ui-toolkit';
import { Box } from '@locus-taxy/encore-mui';
import { createInstance } from '@module-federation/enhanced/runtime';
import ReactDOM from 'react-dom';
import * as ReactRouterDOM from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import { useAuthIntegration } from '../../api/network/auth';
import { useClientId } from '../../hooks/useClientId';
import { CUSTOM_APP_BASE_URL } from '../../config';
import { getTestId } from '@locus-taxy/encore-utils';
import { useRouteContext } from '../../hooks/useRouteContext';
import { TestIdAction } from '../../utils/testIdUtils';

type NetworkMapDetailsMfeProps = {
  entityData?: Record<string, unknown>;
  networkMapId?: string;
  clientId?: string;
  authToken?: string;
};

type NetworkMapDetailsRemoteModule = {
  default: React.ComponentType<NetworkMapDetailsMfeProps>;
};

const mf = createInstance({
  name: 'host',
  remotes: [
    {
      name: 'network-map-details',
      entry: `${CUSTOM_APP_BASE_URL}/network-map/mf-manifest.json`,
    },
  ],
  shared: {
    react: {
      version: '19.1.1',
      scope: 'default',
      lib: () => React,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.1.1',
      },
    },
    'react-dom': {
      version: '19.1.1',
      scope: 'default',
      lib: () => ReactDOM,
      shareConfig: {
        singleton: true,
        requiredVersion: '^19.1.1',
      },
    },
    'react-router-dom': {
      version: '7.6.3',
      scope: 'default',
      lib: () => ReactRouterDOM,
      shareConfig: {
        singleton: true,
        requiredVersion: '^7.6.3',
      },
    },
    '@locus-taxy/encore-mui': {
      version: '1.0.0-beta.28',
      scope: 'default',
      lib: () => MUI,
      shareConfig: {
        singleton: true,
        requiredVersion: '^1.0.0-beta.28',
      },
    },
    '@locus-taxy/encore-ui-network': {
      version: '3.1.0',
      scope: 'default',
      lib: () => Network,
      shareConfig: {
        singleton: true,
        requiredVersion: '^3.1.0',
      },
    },
    '@locus-taxy/encore-ui-toolkit': {
      version: '5.0.0',
      scope: 'default',
      lib: () => Toolkit,
      shareConfig: {
        singleton: true,
        requiredVersion: '^5.0.0',
      },
    },
  },
});

const NetworkMapDetailsLazy = React.lazy(() => {
  return mf.loadRemote('network-map-details/App').then((value: unknown) => {
    const { default: DefaultExport } = value as NetworkMapDetailsRemoteModule;
    const Component = DefaultExport;
    return {
      default: Component as React.ComponentType<NetworkMapDetailsMfeProps>,
    };
  });
});

export const NetworkMapDetailsContainer = ({
  entityData,
  networkMapId,
  hideBorder = false,
}: {
  entityData: Record<string, unknown>;
  networkMapId: string;
  hideBorder?: boolean;
}) => {
  const { token } = useAuthIntegration();
  const clientId = useClientId();
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const { entity, page } = useRouteContext();

  return (
    <Box
      ref={containerRef}
      sx={{
        overflow: 'auto',
        borderRight: hideBorder ? 'none' : '1px solid',
        borderColor: hideBorder ? 'transparent' : 'divider',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
      }}
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'network-map-details-container',
        entity: entity ?? 'network-map',
        page: page ?? 'details',
      })}
    >
      <React.Suspense fallback={<LoadingSpinner />}>
        <NetworkMapDetailsLazy
          entityData={entityData}
          networkMapId={networkMapId}
          clientId={clientId}
          authToken={token ?? undefined}
        />
      </React.Suspense>
    </Box>
  );
};
